import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { formatMoneyNumeral } from '../../util/helpers/numeralHelpers';
import { variables } from '../../assets/styles/variables';
import themeColors from '../../assets/styles/themeColors';
import { mediaBelow, pxToRem, pxToRemMd } from '../../assets/styles/helper';

const AvailableBalanceCardContainer = styled.div`
  border-radius: ${variables.borderRadius.borderRadiusMdPlus};
  background-color: ${themeColors.colorBackgroundPrimary};
  box-shadow: 0px 3px 9px 0px ${themeColors.colorShadowCard};
  border: 1px solid ${themeColors.colorWhite};
  padding: ${pxToRem(16)};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    padding: ${pxToRemMd(16)} ${pxToRemMd(16)} ${pxToRemMd(12)};
  }
`;

const AvailableBalanceHeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(10)};
  padding-bottom: ${pxToRem(12.5)};
  border-bottom: 1px solid ${themeColors.colorBorderPrimary};
  margin-bottom: ${pxToRem(24)};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    padding-bottom: ${pxToRemMd(12)};
    margin-bottom: ${pxToRemMd(24)};
    flex-wrap: wrap;
  }
`;

const AvailableBalanceItem = styled.div`
  & + & {
    margin-top: ${pxToRem(24)};
  }
`;

const AvailableBalanceItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${pxToRem(10)};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    flex-wrap: wrap;
  }
`;

const AvailableBalanceTitle = styled.p`
  font-family: ${variables.family.goldSilver};
  font-size: 16px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0;
  color: ${themeColors.colorTextSecondary};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    font-size: 18px;
    line-height: 18px;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-size: 16px;
  }
`;

const AvailableBalanceAmount = styled.p`
  font-family: ${variables.family.goldSilver};
  font-size: 18px;
  font-weight: 600;
  line-height: 22px;
  letter-spacing: 0;
  text-align: right;
  color: ${({ isPrimary }) =>
    isPrimary ? themeColors.colorTextPrimary : themeColors.colorTextSecondary};

  ${mediaBelow(variables.breakpoints.bpXl)} {
    font-size: 18px;
    line-height: 22px;
  }

  ${mediaBelow(variables.breakpoints.bpMd)} {
    font-weight: 500;
  }
`;

const AvailableBalanceDescription = styled.p`
  font-family: ${variables.family.goldSilver};
  font-size: 14px;
  font-weight: 400;
  line-height: 18px;
  letter-spacing: 0;
  color: ${themeColors.colorTextSecondary};
  margin: ${pxToRem(4)} 0 0;

  ${mediaBelow(variables.breakpoints.bpXl)} {
    font-size: 14px;
    line-height: 18px;
  }
`;

const AvailableBalanceCard = ({
  title,
  balance,
  tradingTitle,
  tradingBalance,
  tradingDescription,
  withdrawalTitle,
  withdrawalBalance,
  withdrawalDescription,
}) => (
  <AvailableBalanceCardContainer>
    <AvailableBalanceHeaderRow>
      <AvailableBalanceTitle>{title}</AvailableBalanceTitle>
      <AvailableBalanceAmount isPrimary>
        {formatMoneyNumeral(balance)}
      </AvailableBalanceAmount>
    </AvailableBalanceHeaderRow>
    <AvailableBalanceItem>
      <AvailableBalanceItemRow>
        <AvailableBalanceTitle>{tradingTitle}</AvailableBalanceTitle>
        <AvailableBalanceAmount>
          {formatMoneyNumeral(tradingBalance)}
        </AvailableBalanceAmount>
      </AvailableBalanceItemRow>
      <AvailableBalanceDescription>
        {tradingDescription}
      </AvailableBalanceDescription>
    </AvailableBalanceItem>
    <AvailableBalanceItem>
      <AvailableBalanceItemRow>
        <AvailableBalanceTitle>{withdrawalTitle}</AvailableBalanceTitle>
        <AvailableBalanceAmount>
          {formatMoneyNumeral(withdrawalBalance)}
        </AvailableBalanceAmount>
      </AvailableBalanceItemRow>
      <AvailableBalanceDescription>
        {withdrawalDescription}
      </AvailableBalanceDescription>
    </AvailableBalanceItem>
  </AvailableBalanceCardContainer>
);

AvailableBalanceCard.propTypes = {
  title: PropTypes.string,
  balance: PropTypes.number,
  tradingTitle: PropTypes.string,
  tradingBalance: PropTypes.number,
  tradingDescription: PropTypes.string,
  withdrawalTitle: PropTypes.string,
  withdrawalBalance: PropTypes.number,
  withdrawalDescription: PropTypes.string,
};

export default AvailableBalanceCard;
